import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import api from "@/lib/api";
import { ArrowLeft, Printer, X, Settings as Cog, FileText } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TERMS = `E. & O.E.
1. Goods once sold will not be taken back.
2. Interest @ 18% p.a. will be charged if the payment is not made with in the stipulated time.
3. Subject to 'Pune' Jurisdiction only.`;

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [inv, setInv] = useState(null);

  // Public (QR-scan) mode: no auth required, hides edit controls
  const isPublic = location.pathname.startsWith("/p/invoice/");

  // Print options
  const [printTerms, setPrintTerms] = useState(true);
  const [printSignature, setPrintSignature] = useState(true);
  const [printBank, setPrintBank] = useState(true);
  const [copyType, setCopyType] = useState("Original Copy");
  const [printMode, setPrintMode] = useState("invoice"); // 'invoice' | 'eway' | 'both'

  // Editable Terms & Conditions (defaults seeded from user-provided list)
  const [termsText, setTermsText] = useState(DEFAULT_TERMS);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsDraft, setTermsDraft] = useState(DEFAULT_TERMS);

  // Auto-generated QR (encodes a public-shareable link to this invoice)
  const [autoQR, setAutoQR] = useState("");

  // E-Invoice info edit modal
  const [eiOpen, setEiOpen] = useState(false);
  const [eiTab, setEiTab] = useState("einvoice"); // 'einvoice' | 'ewaybill'
  const [ei, setEi] = useState({ irn: "", ack_no: "", ack_date: "", eway_bill: "", qr_image: "" });
  const [ewb, setEwb] = useState({
    ewb_no: "", ewb_generated_date: "", ewb_valid_from: "", ewb_valid_until: "", ewb_generated_by: "",
    supply_type: "Outward", sub_type: "Supply", document_type: "Tax Invoice", document_no: "", document_date: "",
    from_gstin: "", from_trade_name: "", from_address: "", from_place: "", from_pincode: "", from_state: "",
    to_gstin: "", to_trade_name: "", to_address: "", to_place: "", to_pincode: "", to_state: "",
    transporter_id: "", transporter_name: "", transporter_doc_no: "", transporter_doc_date: "",
    transport_mode: "Road", vehicle_type: "Regular", vehicle_no: "", distance_km: 0,
    ewb_qr_image: "",
  });
  const [savingEi, setSavingEi] = useState(false);
  const [savingEwb, setSavingEwb] = useState(false);

  const hasEwbData = !!(inv?.eway && (inv.eway.ewb_no || inv.eway.transporter_id || inv.eway.vehicle_no));

  const doPrint = (mode) => {
    setPrintMode(mode);
    // give React a tick to apply the class before opening the system dialog
    setTimeout(() => window.print(), 50);
  };

  const load = async () => {
    const { data } = isPublic
      ? await axios.get(`${API_BASE}/sales/invoices/public/${id}`)
      : await api.get(`/sales/invoices/${id}`);
    setInv(data);
    setEi({
      irn: data.irn || "",
      ack_no: data.ack_no || "",
      ack_date: data.ack_date || "",
      eway_bill: data.eway_bill || "",
      qr_image: data.qr_image || "",
    });
    // Seed Terms text from company settings if available, else default
    const seededTerms = (data.company && data.company.invoice_terms && data.company.invoice_terms.trim())
      ? data.company.invoice_terms
      : DEFAULT_TERMS;
    setTermsText(seededTerms);
    setTermsDraft(seededTerms);
    if (data.eway) {
      setEwb((prev) => ({ ...prev, ...data.eway }));
    } else {
      // Pre-fill EWB with invoice + customer data so the user starts close to ready
      setEwb((prev) => ({
        ...prev,
        document_no: data.invoice_no || "",
        document_date: data.invoice_date || "",
        from_gstin: data.company?.gst || "",
        from_trade_name: data.company?.name || "",
        from_address: data.company?.address || "",
        to_gstin: data.ship_to_info?.gst || data.customer_info?.gst || "",
        to_trade_name: data.ship_to_info?.name || data.customer_info?.name || data.customer || "",
        to_address: data.ship_to_info?.address || data.customer_info?.address || "",
        vehicle_no: prev.vehicle_no || data.vehicle_no || "",
        transporter_name: prev.transporter_name || data.transport_name || "",
      }));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-generate QR linking to a public, shareable summary of this invoice.
  // The QR encodes a URL anyone can open (no auth required) to view the invoice.
  useEffect(() => {
    if (!inv) return;
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const publicUrl = `${origin}/p/invoice/${id}`;
    const payload = inv.irn
      ? publicUrl + `?irn=${encodeURIComponent(inv.irn)}`
      : publicUrl;
    QRCode.toDataURL(payload, { margin: 1, width: 256, errorCorrectionLevel: "M" })
      .then((dataUrl) => setAutoQR(dataUrl))
      .catch(() => setAutoQR(""));
  }, [inv, id]);

  // If the user came in via /sales/invoice/print/<id>?print=invoice|eway → auto-trigger
  useEffect(() => {
    if (!inv) return;
    const sp = new URLSearchParams(location.search);
    const want = sp.get("print");
    if (want === "invoice" || want === "eway") {
      // strip the query param so a refresh doesn't re-print
      navigate(location.pathname, { replace: true });
      doPrint(want);
    }
    // eslint-disable-next-line
  }, [inv]);

  const onSaveEi = async () => {
    setSavingEi(true);
    try {
      await api.put(`/sales/invoices/${id}/e-invoice-info`, ei);
      toast.success("E-Invoice info saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingEi(false);
    }
  };

  const onSaveEwb = async () => {
    setSavingEwb(true);
    try {
      await api.put(`/sales/invoices/${id}/eway-bill`, ewb);
      toast.success("E-Way Bill saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingEwb(false);
    }
  };

  const onUploadQR = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) return toast.error("QR too large (max 500 KB)");
    const reader = new FileReader();
    reader.onload = () => setEi((p) => ({ ...p, qr_image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onUploadEwbQR = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) return toast.error("QR too large (max 500 KB)");
    const reader = new FileReader();
    reader.onload = () => setEwb((p) => ({ ...p, ewb_qr_image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const intra = inv?.intra_state !== false;

  // Unified line items — prefer the multi-item array, fall back to the
  // single-line invoice shape for legacy invoices.
  const lines = useMemo(() => {
    if (!inv) return [];
    if (Array.isArray(inv.items) && inv.items.length > 0) return inv.items;
    return [{
      part_no: inv.part_no,
      part_name: inv.part_name,
      hsn_code: inv.hsn_code,
      dispatch_qty: Number(inv.dispatch_qty || 0),
      part_rate: Number(inv.part_rate || 0),
      dispatch_value: Number(inv.dispatch_value || 0),
      gst_pct: Number(inv.gst_pct || 0),
      gst_amount: Number(inv.gst_amount || 0),
      cgst_pct: Number(inv.cgst_pct || 0), cgst_amount: Number(inv.cgst_amount || 0),
      sgst_pct: Number(inv.sgst_pct || 0), sgst_amount: Number(inv.sgst_amount || 0),
      igst_pct: Number(inv.igst_pct || 0), igst_amount: Number(inv.igst_amount || 0),
      line_total: Number(inv.invoice_value || 0),
    }];
  }, [inv]);

  // Sub-total / tax accumulators for the items table footer
  const subTotal = useMemo(() => lines.reduce((s, r) => s + Number(r.dispatch_value || 0), 0), [lines]);
  const totalQty = useMemo(() => lines.reduce((s, r) => s + Number(r.dispatch_qty || 0), 0), [lines]);
  const totalCgst = useMemo(() => lines.reduce((s, r) => s + Number(r.cgst_amount || 0), 0), [lines]);
  const totalSgst = useMemo(() => lines.reduce((s, r) => s + Number(r.sgst_amount || 0), 0), [lines]);
  const totalIgst = useMemo(() => lines.reduce((s, r) => s + Number(r.igst_amount || 0), 0), [lines]);

  // Tax summary table — group by HSN + Rate so identical-rate items aggregate.
  const taxRows = useMemo(() => {
    if (!inv) return [];
    const buckets = new Map();
    for (const r of lines) {
      const hsn = r.hsn_code || "—";
      const rate = Number(r.gst_pct || 0);
      const key = `${hsn}|${rate}`;
      const b = buckets.get(key) || {
        hsn, taxable: 0,
        cgst_pct: Number(r.cgst_pct || 0), cgst_amt: 0,
        sgst_pct: Number(r.sgst_pct || 0), sgst_amt: 0,
        igst_pct: Number(r.igst_pct || 0), igst_amt: 0,
        total: 0,
      };
      b.taxable += Number(r.dispatch_value || 0);
      b.cgst_amt += Number(r.cgst_amount || 0);
      b.sgst_amt += Number(r.sgst_amount || 0);
      b.igst_amt += Number(r.igst_amount || 0);
      b.total += Number(r.gst_amount || 0);
      buckets.set(key, b);
    }
    return Array.from(buckets.values());
  }, [inv, lines]);

  if (!inv) return <div className="p-8 text-sm text-slate-500">Loading invoice…</div>;

  const company = inv.company || {};
  const billTo = inv.customer_info || {};
  const shipTo = inv.ship_to_info || {};

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      {/* ===== Toolbar (modal-like top bar) — hidden when printing ===== */}
      <div className="no-print sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1100px] mx-auto px-5 py-3 flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="invoice-back-btn"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div className="text-xs text-slate-400">/</div>
          <div className="text-sm font-semibold text-slate-900">
            Invoice <span className="font-mono">{inv.invoice_no}</span>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <select value={copyType} onChange={(e) => setCopyType(e.target.value)}
                data-testid="copy-type-select"
                className="border border-slate-300 rounded-md px-2 py-1 text-xs">
                <option>Original Copy</option>
                <option>Duplicate Copy</option>
                <option>Triplicate Copy</option>
                <option>Office Copy</option>
              </select>
            </label>
            <Toggle label="Terms" testid="toggle-terms" checked={printTerms} onChange={setPrintTerms} />
            <Toggle label="Signature" testid="toggle-signature" checked={printSignature} onChange={setPrintSignature} />
            <Toggle label="Bank" testid="toggle-bank" checked={printBank} onChange={setPrintBank} />
            {!isPublic && (
              <button onClick={() => { setTermsDraft(termsText); setTermsOpen(true); }} data-testid="edit-terms-btn"
                className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white inline-flex items-center gap-1.5">
                <FileText size={12} /> Edit Terms
              </button>
            )}
            {!isPublic && (
              <button onClick={() => setEiOpen(true)} data-testid="einvoice-btn"
                className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-[#1D2951] text-[#1D2951] hover:bg-[#1D2951] hover:text-white inline-flex items-center gap-1.5">
                <Cog size={12} /> E-Invoice / E-Way Bill
              </button>
            )}

            {/* Print options: 3 distinct buttons */}
            <div className="inline-flex rounded-md shadow-sm overflow-hidden" data-testid="print-options">
              <button onClick={() => doPrint("invoice")}
                data-testid="print-invoice-only-btn"
                className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 border-r border-white/20">
                <Printer size={13} /> Print Invoice
              </button>
              <button onClick={() => doPrint("eway")}
                disabled={!hasEwbData}
                title={!hasEwbData ? "Save E-Way Bill data first (E-Invoice / E-Way Bill button)" : "Print only the NIC E-Way Bill page"}
                data-testid="print-eway-only-btn"
                className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 border-r border-white/20">
                <Printer size={13} /> Print E-Way Bill
              </button>
              <button onClick={() => doPrint("both")}
                disabled={!hasEwbData}
                title={!hasEwbData ? "Save E-Way Bill data first" : "Print Invoice + E-Way Bill (2 pages)"}
                data-testid="print-both-btn"
                className="bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
                <Printer size={13} /> Print Both
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Invoice paper (A4 portrait ~ 794px) ===== */}
      <div className={`max-w-[794px] mx-auto p-6 print:p-0 print-block-invoice`} data-print-section="invoice">
        <div className="no-print text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Document 1 of {hasEwbData ? "2" : "1"} · Tax Invoice</div>
        <div
          className="bg-white border border-slate-900 text-slate-900 text-[12px] leading-tight print:border-slate-900"
          data-testid="invoice-print-page"
          style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }}
        >
          {/* Header row */}
          <div className="flex border-b border-slate-900">
            <div className="flex-1 p-3 flex items-start gap-3 border-r border-slate-900">
              {company.logo ? (
                <img src={company.logo} alt="Logo" className="w-16 h-16 object-contain shrink-0" />
              ) : null}
              <div className="flex-1">
                <div className="text-lg font-bold uppercase tracking-tight">{company.name || "Your Company"}</div>
                <div className="text-[11px] mt-0.5 whitespace-pre-line">{company.address}</div>
                <div className="text-[11px] mt-0.5">
                  <span className="font-semibold">GSTIN :</span> {company.gst || "—"}
                  {company.msme_no && <> <span className="font-semibold ml-2">MSME No. :</span> {company.msme_no}</>}
                </div>
                <div className="text-[11px] mt-0.5">
                  <span className="font-semibold">Tel.: </span>{company.contact || "—"}
                  {company.email && <> <span className="font-semibold ml-2">email :</span> {company.email}</>}
                </div>
              </div>
            </div>
            <div className="w-44 p-3 text-center">
              <div className="border border-slate-900 px-2 py-0.5 text-[11px] font-semibold uppercase mb-2">{copyType}</div>
              <div className="text-base font-bold uppercase tracking-wider">Tax Invoice</div>
            </div>
          </div>

          {/* Invoice / PO / Transport row */}
          <div className="flex border-b border-slate-900">
            <div className="flex-1 border-r border-slate-900">
              <KV label="Invoice No." value={inv.invoice_no} bold />
              <KV label="Dated" value={inv.invoice_date} />
              <KV label="DN" value={inv.dn || "—"} />
              <KV label="Place of Supply" value={inv.ship_to_info?.place_of_supply || inv.bill_to_place_of_supply || inv.ship_to_place_of_supply || inv.customer_info?.place_of_supply || "—"} />
              <KV label="Reverse Charge" value={inv.reverse_charge || "N"} />
              <KV label="Vendor Code" value={inv.vendor_code || ""} last />
            </div>
            <div className="flex-1">
              <KV label="PO No." value={inv.po_no} />
              <KV label="Transport" value={inv.transport_name || ""} />
              <KV label="Vehicle No." value={inv.vehicle_no || ""} />
              <KV label="Station" value={inv.station || ""} />
              <KV label="E-Way Bill" value={inv.eway_bill || ""} last />
            </div>
          </div>

          {/* Bill to / Ship to */}
          <div className="flex border-b border-slate-900">
            <div className="flex-1 p-2 border-r border-slate-900">
              <div className="text-[11px] font-semibold">Billed to :</div>
              <div className="font-bold mt-0.5">{billTo.name || inv.customer}</div>
              <div className="whitespace-pre-line text-[11px]">{billTo.address}</div>
              {billTo.gst && <div className="text-[11px] mt-1"><span className="font-semibold">GSTIN / UIN :</span> <span className="font-mono">{billTo.gst}</span></div>}
            </div>
            <div className="flex-1 p-2">
              <div className="text-[11px] font-semibold">Shipped to :</div>
              <div className="font-bold mt-0.5">{shipTo.name || billTo.name || inv.customer}</div>
              <div className="whitespace-pre-line text-[11px]">{shipTo.address || billTo.address}</div>
              {(shipTo.gst || billTo.gst) && <div className="text-[11px] mt-1"><span className="font-semibold">GSTIN / UIN :</span> <span className="font-mono">{shipTo.gst || billTo.gst}</span></div>}
            </div>
          </div>

          {/* IRN row */}
          <div className="flex border-b border-slate-900 text-[11px]">
            <div className="flex-1 px-2 py-1 border-r border-slate-900"><span className="font-semibold">IRN :</span> <span className="font-mono break-all">{inv.irn || ""}</span></div>
            <div className="w-48 px-2 py-1 border-r border-slate-900"><span className="font-semibold">Ack.No. :</span> {inv.ack_no || ""}</div>
            <div className="w-44 px-2 py-1"><span className="font-semibold">Ack. Date :</span> {inv.ack_date || ""}</div>
          </div>

          {/* Item table */}
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <Th w="40" align="center">S.N.</Th>
                <Th w="110">Item Part No</Th>
                <Th>Description of Goods</Th>
                <Th w="80" align="center">HSN/SAC</Th>
                <Th w="60" align="right">Qty.</Th>
                <Th w="50" align="center">Unit</Th>
                <Th w="80" align="right">Price</Th>
                <Th w="100" align="right">Amount (₹)</Th>
              </tr>
            </thead>
            <tbody>
              {lines.map((row, idx) => (
                <tr key={idx}>
                  <Td align="center" className="font-mono">{idx + 1}.</Td>
                  <Td className="font-mono">{row.part_no}</Td>
                  <Td>
                    <div className="font-semibold">{row.part_name || row.part_no}</div>
                  </Td>
                  <Td align="center" className="font-mono">{row.hsn_code || "—"}</Td>
                  <Td align="right" className="font-mono">{Number(row.dispatch_qty || 0).toFixed(2)}</Td>
                  <Td align="center">Nos</Td>
                  <Td align="right" className="font-mono">{fmt(row.part_rate)}</Td>
                  <Td align="right" className="font-mono">{fmt(row.dispatch_value)}</Td>
                </tr>
              ))}
              {Array.from({ length: Math.max(0, 6 - lines.length + 1) }).map((_, i) => (
                <tr key={`pad-${i}`}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <Td key={j}>&nbsp;</Td>
                  ))}
                </tr>
              ))}
              {/* Totals */}
              <tr>
                <Td colSpan={7} align="right" className="font-semibold">Sub Total</Td>
                <Td align="right" className="font-mono font-semibold">{fmt(subTotal)}</Td>
              </tr>
              {intra ? (
                <>
                  <tr>
                    <Td colSpan={7} align="right">Add : CGST</Td>
                    <Td align="right" className="font-mono">{fmt(totalCgst)}</Td>
                  </tr>
                  <tr>
                    <Td colSpan={7} align="right">Add : SGST</Td>
                    <Td align="right" className="font-mono">{fmt(totalSgst)}</Td>
                  </tr>
                </>
              ) : (
                <tr>
                  <Td colSpan={7} align="right">Add : IGST</Td>
                  <Td align="right" className="font-mono">{fmt(totalIgst)}</Td>
                </tr>
              )}
              <tr className="bg-slate-100 font-bold">
                <Td colSpan={4} align="right">Grand Total</Td>
                <Td align="right" className="font-mono">{Number(totalQty || 0).toFixed(2)} Nos</Td>
                <Td colSpan={2}></Td>
                <Td align="right" className="font-mono">{fmt(inv.invoice_value)}</Td>
              </tr>
            </tbody>
          </table>

          {/* Amount in words */}
          <div className="border-t border-slate-900 px-2 py-1 text-[11px]">
            <span className="font-semibold">Rupees</span> {inv.amount_in_words || "—"}
          </div>

          {/* Tax summary table */}
          <table className="w-full text-[11px] border-collapse border-t border-slate-900">
            <thead>
              <tr className="bg-slate-100">
                <Th align="center" w="80">HSN/SAC</Th>
                <Th align="right">Taxable Amt.</Th>
                {intra ? (
                  <>
                    <Th align="center" w="60">CGST Rate</Th>
                    <Th align="right">CGST Amt.</Th>
                    <Th align="center" w="60">SGST Rate</Th>
                    <Th align="right">SGST Amt.</Th>
                  </>
                ) : (
                  <>
                    <Th align="center" w="60">IGST Rate</Th>
                    <Th align="right">IGST Amt.</Th>
                  </>
                )}
                <Th align="right">Total Tax</Th>
              </tr>
            </thead>
            <tbody>
              {taxRows.map((r, i) => (
                <tr key={i}>
                  <Td align="center" className="font-mono">{r.hsn}</Td>
                  <Td align="right" className="font-mono">{fmt(r.taxable)}</Td>
                  {intra ? (
                    <>
                      <Td align="center">{r.cgst_pct}%</Td>
                      <Td align="right" className="font-mono">{fmt(r.cgst_amt)}</Td>
                      <Td align="center">{r.sgst_pct}%</Td>
                      <Td align="right" className="font-mono">{fmt(r.sgst_amt)}</Td>
                    </>
                  ) : (
                    <>
                      <Td align="center">{r.igst_pct}%</Td>
                      <Td align="right" className="font-mono">{fmt(r.igst_amt)}</Td>
                    </>
                  )}
                  <Td align="right" className="font-mono font-semibold">{fmt(r.total)}</Td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Optional Remark line (printed only if provided on dispatch) */}
          {inv.remark && (
            <div className="border-t border-slate-900 px-2 py-1.5 text-[11px]">
              <span className="font-semibold">Remark:</span> <span className="text-slate-800">{inv.remark}</span>
            </div>
          )}

          {/* Footer row: bank/terms | QR + signature */}
          <div className="flex border-t border-slate-900 text-[11px]">
            <div className="flex-1 border-r border-slate-900">
              {printBank && company.bank_details && (
                <div className="border-b border-slate-900 p-2">
                  <div className="font-semibold">Bank Details :</div>
                  <div className="whitespace-pre-line">{company.bank_details}</div>
                </div>
              )}
              {printTerms && (
                <div className="p-2">
                  <div className="font-semibold">Terms &amp; Conditions</div>
                  <div className="whitespace-pre-line text-[10.5px] mt-0.5">
                    {termsText || DEFAULT_TERMS}
                  </div>
                </div>
              )}
            </div>
            <div className="w-72 flex flex-col">
              <div className="border-b border-slate-900 p-2 text-center">
                <div className="font-semibold mb-1">E-Invoice QR Code</div>
                <div className="border border-slate-300 h-28 flex items-center justify-center bg-slate-50">
                  {inv.qr_image ? (
                    <img src={inv.qr_image} alt="QR" className="h-24 w-24 object-contain" />
                  ) : autoQR ? (
                    <img src={autoQR} alt="Invoice QR" className="h-24 w-24 object-contain" data-testid="auto-invoice-qr" />
                  ) : (
                    <span className="text-slate-400 text-[10px]">Generating QR…</span>
                  )}
                </div>
                <div className="text-[9px] text-slate-500 mt-1">Scan to view invoice</div>
              </div>
              <div className="p-2">
                <div className="text-[10px] text-slate-600 mb-1">Receiver's Signature :</div>
                <div className="h-10"></div>
                <div className="border-t border-slate-400 mt-1 pt-1 text-right">
                  <div className="text-[10px] italic">for {company.name}</div>
                  {printSignature && company.e_signature && (
                    <img src={company.e_signature} alt="Signature" className="h-12 inline-block object-contain mt-1" />
                  )}
                  <div className="text-[10.5px] font-semibold mt-1">Authorised Signatory</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== NIC-style E-Way Bill (prints on page 2 when "both", or solo when "eway") ===== */}
      {hasEwbData && (
        <div className={`max-w-[794px] mx-auto p-6 print:p-0 print-block-eway`} data-print-section="eway">
          <div className="no-print text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-2">Document 2 of 2 · NIC E-Way Bill</div>
          <div className="bg-white border border-slate-900 text-slate-900 text-[11px] leading-tight print-eway-page" data-testid="eway-bill-print"
            style={{ fontFamily: '"Segoe UI", Arial, sans-serif' }}>
            <div className="border-b border-slate-900 px-3 py-2 flex items-center justify-between bg-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-600">e-Way Bill System · National Informatics Centre</div>
                <div className="text-base font-bold uppercase tracking-tight">e-Way Bill</div>
              </div>
              <div className="text-right text-[10px]">
                <div><span className="font-semibold">e-Way Bill No :</span> <span className="font-mono">{inv.eway.ewb_no || "—"}</span></div>
                <div><span className="font-semibold">Generated Date :</span> <span className="font-mono">{inv.eway.ewb_generated_date || "—"}</span></div>
                <div><span className="font-semibold">Valid From :</span> <span className="font-mono">{inv.eway.ewb_valid_from || "—"}</span></div>
                <div><span className="font-semibold">Valid Until :</span> <span className="font-mono">{inv.eway.ewb_valid_until || "—"}</span></div>
              </div>
            </div>

            <div className="border-b border-slate-900">
              <div className="bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase border-b border-slate-900">1. e-Way Bill Details (Part-A)</div>
              <div className="grid grid-cols-4">
                <NICCell label="Supply Type" value={inv.eway.supply_type} />
                <NICCell label="Sub Type" value={inv.eway.sub_type} />
                <NICCell label="Document Type" value={inv.eway.document_type} />
                <NICCell label="Document No." value={inv.eway.document_no} />
                <NICCell label="Document Date" value={inv.eway.document_date} />
                <NICCell label="Generated By (GSTIN)" value={inv.eway.ewb_generated_by} mono />
                <NICCell label="From GSTIN" value={inv.eway.from_gstin} mono />
                <NICCell label="To GSTIN" value={inv.eway.to_gstin} mono last />
              </div>
            </div>

            <div className="flex border-b border-slate-900">
              <div className="flex-1 p-2 border-r border-slate-900">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">2. From Address (Dispatch From)</div>
                <div className="font-bold mt-1">{inv.eway.from_trade_name || "—"}</div>
                <div className="whitespace-pre-line">{inv.eway.from_address}</div>
                <div className="text-[10px] mt-0.5">
                  {inv.eway.from_place && <>{inv.eway.from_place} · </>}
                  {inv.eway.from_pincode && <span className="font-mono">PIN {inv.eway.from_pincode} · </span>}
                  {inv.eway.from_state}
                </div>
              </div>
              <div className="flex-1 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">3. To Address (Ship To)</div>
                <div className="font-bold mt-1">{inv.eway.to_trade_name || "—"}</div>
                <div className="whitespace-pre-line">{inv.eway.to_address}</div>
                <div className="text-[10px] mt-0.5">
                  {inv.eway.to_place && <>{inv.eway.to_place} · </>}
                  {inv.eway.to_pincode && <span className="font-mono">PIN {inv.eway.to_pincode} · </span>}
                  {inv.eway.to_state}
                </div>
              </div>
            </div>

            <div className="border-b border-slate-900">
              <div className="bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase border-b border-slate-900">4. Goods Details</div>
              <table className="w-full text-[11px] border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <Th align="center" w="50">S.N.</Th>
                    <Th align="center" w="90">HSN Code</Th>
                    <Th>Product Name &amp; Description</Th>
                    <Th align="right" w="70">Qty</Th>
                    <Th align="right" w="100">Taxable Amt (₹)</Th>
                    <Th align="right" w="80">Tax</Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((row, idx) => (
                    <tr key={idx}>
                      <Td align="center" className="font-mono">{idx + 1}.</Td>
                      <Td align="center" className="font-mono">{row.hsn_code || "—"}</Td>
                      <Td>{row.part_name || row.part_no} <span className="text-[10px] text-slate-500">({row.part_no})</span></Td>
                      <Td align="right" className="font-mono">{Number(row.dispatch_qty || 0).toFixed(2)} Nos</Td>
                      <Td align="right" className="font-mono">{fmt(row.dispatch_value)}</Td>
                      <Td align="right" className="font-mono">
                        {intra
                          ? `${fmt((row.cgst_amount || 0) + (row.sgst_amount || 0))} (${row.cgst_pct || 0}+${row.sgst_pct || 0}%)`
                          : `${fmt(row.igst_amount)} (${row.igst_pct || 0}%)`}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold bg-slate-100">
                    <Td colSpan={4} align="right">Total Invoice Value</Td>
                    <Td colSpan={2} align="right" className="font-mono">₹ {fmt(inv.invoice_value)}</Td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="border-b border-slate-900">
              <div className="bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase border-b border-slate-900">5. Transportation Details (Part-B)</div>
              <div className="grid grid-cols-4">
                <NICCell label="Transporter ID" value={inv.eway.transporter_id} mono />
                <NICCell label="Transporter Name" value={inv.eway.transporter_name} />
                <NICCell label="Mode" value={inv.eway.transport_mode} />
                <NICCell label="Vehicle Type" value={inv.eway.vehicle_type} />
                <NICCell label="Vehicle No." value={inv.eway.vehicle_no} mono />
                <NICCell label="Approx Distance" value={inv.eway.distance_km ? `${inv.eway.distance_km} km` : ""} />
                <NICCell label="Doc No. (LR/RR/AWB)" value={inv.eway.transporter_doc_no} />
                <NICCell label="Doc Date" value={inv.eway.transporter_doc_date} last />
              </div>
            </div>

            <div className="flex">
              <div className="flex-1 p-3 border-r border-slate-900">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</div>
                <ul className="text-[10px] text-slate-700 mt-1 list-disc pl-4 space-y-0.5">
                  <li>This e-Way Bill is valid for the period mentioned above.</li>
                  <li>It is mandatory to carry this e-Way Bill in physical / electronic form during transit.</li>
                  <li>Generated and validated through the Government of India NIC e-Way Bill portal (ewaybillgst.gov.in).</li>
                </ul>
              </div>
              <div className="w-56 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">e-Way Bill QR</div>
                <div className="border border-slate-300 h-28 flex items-center justify-center bg-slate-50">
                  {inv.eway.ewb_qr_image ? (
                    <img src={inv.eway.ewb_qr_image} alt="EWB QR" className="h-24 w-24 object-contain" />
                  ) : autoQR ? (
                    <img src={autoQR} alt="EWB QR" className="h-24 w-24 object-contain" />
                  ) : (
                    <span className="text-slate-400 text-[10px]">Generating QR…</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {termsOpen && (
        <Modal title="Edit Terms & Conditions (before printing)" onClose={() => setTermsOpen(false)}>
          <div className="space-y-3">
            <div className="text-[11px] text-slate-500">
              These terms will appear at the bottom of the invoice. Edits apply to this print only — they do not save permanently.
            </div>
            <textarea
              value={termsDraft}
              onChange={(e) => setTermsDraft(e.target.value)}
              data-testid="terms-textarea"
              rows={8}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono whitespace-pre-wrap"
            />
            <div className="flex justify-between items-center pt-2">
              <button
                onClick={() => setTermsDraft(DEFAULT_TERMS)}
                data-testid="terms-reset-btn"
                className="text-xs text-slate-600 hover:text-slate-900 underline"
              >
                Reset to default
              </button>
              <div className="flex gap-2">
                <button onClick={() => setTermsOpen(false)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
                <button
                  onClick={() => { setTermsText(termsDraft); setTermsOpen(false); toast.success("Terms updated for this print"); }}
                  data-testid="terms-save-btn"
                  className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-4 py-1.5 rounded-md"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {eiOpen && (
        <Modal title="E-Invoice & E-Way Bill" onClose={() => setEiOpen(false)} wide>
          {/* Tabs */}
          <div className="flex border-b border-slate-200 -mx-6 px-6 mb-4">
            <TabBtn active={eiTab === "einvoice"} onClick={() => setEiTab("einvoice")} testid="tab-einvoice">E-Invoice (IRN)</TabBtn>
            <TabBtn active={eiTab === "ewaybill"} onClick={() => setEiTab("ewaybill")} testid="tab-ewaybill">NIC E-Way Bill</TabBtn>
          </div>

          {eiTab === "einvoice" && (
            <div className="space-y-3" data-testid="einvoice-tab">
              <div className="text-xs text-slate-500">Add or update the IRN / Acknowledgement / QR after generating the e-invoice on the GST portal.</div>
              <FieldRow label="IRN">
                <input value={ei.irn} onChange={(e) => setEi({ ...ei, irn: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="64-char alphanumeric" data-testid="ei-irn" />
              </FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Ack. No.">
                  <input value={ei.ack_no} onChange={(e) => setEi({ ...ei, ack_no: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" data-testid="ei-ack-no" />
                </FieldRow>
                <FieldRow label="Ack. Date">
                  <input type="date" value={ei.ack_date} onChange={(e) => setEi({ ...ei, ack_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="ei-ack-date" />
                </FieldRow>
              </div>
              <FieldRow label="QR Code Image (from GST portal PDF)">
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={onUploadQR} data-testid="ei-qr-upload" />
                  {ei.qr_image && <img src={ei.qr_image} alt="QR preview" className="w-16 h-16 border border-slate-200 object-contain" />}
                  {ei.qr_image && (
                    <button onClick={() => setEi({ ...ei, qr_image: "" })} className="text-xs text-rose-600">Remove</button>
                  )}
                </div>
              </FieldRow>
              <div className="flex justify-end gap-2 pt-3">
                <button onClick={() => setEiOpen(false)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Close</button>
                <button onClick={onSaveEi} disabled={savingEi} data-testid="ei-save"
                  className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md">
                  {savingEi ? "Saving…" : "Save E-Invoice Info"}
                </button>
              </div>
            </div>
          )}

          {eiTab === "ewaybill" && (
            <div className="space-y-4" data-testid="ewaybill-tab">
              <div className="text-xs text-slate-500">Enter the NIC <span className="font-semibold">e-Way Bill</span> data after generating it on the Govt portal. All fields exactly as they appear on ewaybillgst.gov.in.</div>

              {/* Section 1 — EWB header */}
              <SectionTitle>1. e-Way Bill (Part-A header)</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="EWB Number (12 digits)">
                  <input value={ewb.ewb_no} onChange={(e) => setEwb({ ...ewb, ewb_no: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="e.g. 991234567890" data-testid="ewb-no" maxLength={12} />
                </FieldRow>
                <FieldRow label="Generated By (GSTIN)">
                  <input value={ewb.ewb_generated_by} onChange={(e) => setEwb({ ...ewb, ewb_generated_by: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="15-digit GSTIN" />
                </FieldRow>
                <FieldRow label="Generated Date / Time">
                  <input type="datetime-local" value={ewb.ewb_generated_date} onChange={(e) => setEwb({ ...ewb, ewb_generated_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="Supply Type">
                  <select value={ewb.supply_type} onChange={(e) => setEwb({ ...ewb, supply_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option>Outward</option>
                    <option>Inward</option>
                  </select>
                </FieldRow>
                <FieldRow label="Sub Type">
                  <select value={ewb.sub_type} onChange={(e) => setEwb({ ...ewb, sub_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    {["Supply", "Export", "Job Work", "SKD/CKD", "Recipient Not Known", "For Own Use", "Exhibition or Fairs", "Line Sales", "Others"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </FieldRow>
                <FieldRow label="Document Type">
                  <select value={ewb.document_type} onChange={(e) => setEwb({ ...ewb, document_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    {["Tax Invoice", "Bill of Supply", "Bill of Entry", "Delivery Challan", "Credit Note", "Others"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </FieldRow>
                <FieldRow label="Document No.">
                  <input value={ewb.document_no} onChange={(e) => setEwb({ ...ewb, document_no: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="Document Date">
                  <input type="date" value={ewb.document_date} onChange={(e) => setEwb({ ...ewb, document_date: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="Valid From">
                  <input type="datetime-local" value={ewb.ewb_valid_from} onChange={(e) => setEwb({ ...ewb, ewb_valid_from: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="Valid Until">
                  <input type="datetime-local" value={ewb.ewb_valid_until} onChange={(e) => setEwb({ ...ewb, ewb_valid_until: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
              </div>

              {/* Section 2 — From */}
              <SectionTitle>2. Dispatch From (From Address)</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="From GSTIN">
                  <input value={ewb.from_gstin} onChange={(e) => setEwb({ ...ewb, from_gstin: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="From Trade Name">
                  <input value={ewb.from_trade_name} onChange={(e) => setEwb({ ...ewb, from_trade_name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="From Address" className="col-span-2">
                  <textarea value={ewb.from_address} onChange={(e) => setEwb({ ...ewb, from_address: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[50px]" />
                </FieldRow>
                <FieldRow label="From Place / City">
                  <input value={ewb.from_place} onChange={(e) => setEwb({ ...ewb, from_place: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="From Pincode">
                  <input value={ewb.from_pincode} onChange={(e) => setEwb({ ...ewb, from_pincode: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="From State">
                  <input value={ewb.from_state} onChange={(e) => setEwb({ ...ewb, from_state: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
              </div>

              {/* Section 3 — To */}
              <SectionTitle>3. Ship To (To Address)</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="To GSTIN">
                  <input value={ewb.to_gstin} onChange={(e) => setEwb({ ...ewb, to_gstin: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="To Trade Name">
                  <input value={ewb.to_trade_name} onChange={(e) => setEwb({ ...ewb, to_trade_name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="To Address" className="col-span-2">
                  <textarea value={ewb.to_address} onChange={(e) => setEwb({ ...ewb, to_address: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[50px]" />
                </FieldRow>
                <FieldRow label="To Place / City">
                  <input value={ewb.to_place} onChange={(e) => setEwb({ ...ewb, to_place: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="To Pincode">
                  <input value={ewb.to_pincode} onChange={(e) => setEwb({ ...ewb, to_pincode: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="To State">
                  <input value={ewb.to_state} onChange={(e) => setEwb({ ...ewb, to_state: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
              </div>

              {/* Section 4 — Part B Transport */}
              <SectionTitle>4. Transportation (Part-B)</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Transporter ID (GSTIN / TRANSIN)">
                  <input value={ewb.transporter_id} onChange={(e) => setEwb({ ...ewb, transporter_id: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" data-testid="ewb-transporter-id" />
                </FieldRow>
                <FieldRow label="Transporter Name">
                  <input value={ewb.transporter_name} onChange={(e) => setEwb({ ...ewb, transporter_name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
                <FieldRow label="Mode of Transport">
                  <select value={ewb.transport_mode} onChange={(e) => setEwb({ ...ewb, transport_mode: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option>Road</option><option>Rail</option><option>Air</option><option>Ship</option>
                  </select>
                </FieldRow>
                <FieldRow label="Vehicle Type">
                  <select value={ewb.vehicle_type} onChange={(e) => setEwb({ ...ewb, vehicle_type: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option>Regular</option><option>Over Dimensional Cargo</option>
                  </select>
                </FieldRow>
                <FieldRow label="Vehicle No.">
                  <input value={ewb.vehicle_no} onChange={(e) => setEwb({ ...ewb, vehicle_no: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="MH12-AB-1234" data-testid="ewb-vehicle" />
                </FieldRow>
                <FieldRow label="Approx Distance (km)">
                  <input type="number" min="0" step="1" value={ewb.distance_km} onChange={(e) => setEwb({ ...ewb, distance_km: Number(e.target.value || 0) })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="Transport Doc No. (LR / RR / AWB)">
                  <input value={ewb.transporter_doc_no} onChange={(e) => setEwb({ ...ewb, transporter_doc_no: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
                </FieldRow>
                <FieldRow label="Transport Doc Date">
                  <input type="date" value={ewb.transporter_doc_date} onChange={(e) => setEwb({ ...ewb, transporter_doc_date: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </FieldRow>
              </div>

              {/* QR */}
              <SectionTitle>5. e-Way Bill QR (from NIC PDF)</SectionTitle>
              <div className="flex items-center gap-3">
                <input type="file" accept="image/*" onChange={onUploadEwbQR} data-testid="ewb-qr-upload" />
                {ewb.ewb_qr_image && <img src={ewb.ewb_qr_image} alt="EWB QR" className="w-16 h-16 border border-slate-200 object-contain" />}
                {ewb.ewb_qr_image && (
                  <button onClick={() => setEwb({ ...ewb, ewb_qr_image: "" })} className="text-xs text-rose-600">Remove</button>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button onClick={() => setEiOpen(false)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Close</button>
                <button onClick={onSaveEwb} disabled={savingEwb} data-testid="ewb-save"
                  className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md">
                  {savingEwb ? "Saving…" : "Save E-Way Bill"}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Print CSS — driven by `printMode` */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { size: A4 portrait; margin: 8mm; }
        }
        /* Hide blocks per selected print mode */
        ${printMode === "invoice" ? `
          @media print { .print-block-eway { display: none !important; } }
        ` : ""}
        ${printMode === "eway" ? `
          @media print { .print-block-invoice { display: none !important; } }
        ` : ""}
        ${printMode === "both" ? `
          @media print { .print-eway-page { break-before: page; page-break-before: always; } }
        ` : ""}
      `}</style>
    </div>
  );
}

const KV = ({ label, value, bold = false, last = false }) => (
  <div className={`flex text-[11px] ${last ? "" : "border-b border-slate-900"}`}>
    <div className="w-32 px-2 py-1 border-r border-slate-900 font-semibold bg-slate-50">{label}</div>
    <div className={`flex-1 px-2 py-1 ${bold ? "font-bold" : ""}`}>{value || ""}</div>
  </div>
);

const Th = ({ children, w, align = "left" }) => (
  <th
    style={w ? { width: `${w}px` } : undefined}
    className={`border border-slate-900 px-2 py-1 text-${align} text-[11px] font-semibold`}
  >{children}</th>
);
const Td = ({ children, align = "left", className = "", colSpan }) => (
  <td colSpan={colSpan} className={`border border-slate-900 px-2 py-0.5 align-top text-${align} ${className}`}>{children}</td>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
      data-testid={testid}
      className="h-4 w-4 rounded border-slate-400 text-[#1D2951] focus:ring-[#1D2951]" />
    <span className="font-semibold uppercase tracking-wider text-slate-700">{label}</span>
  </label>
);

const FieldRow = ({ label, children, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
    {children}
  </div>
);

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className={`bg-white rounded-lg shadow-xl border border-slate-200 w-full ${wide ? "max-w-3xl" : "max-w-xl"} p-6 max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-slate-900">{title}</div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const NICCell = ({ label, value, mono = false, last = false }) => (
  <div className={`p-2 ${last ? "" : "border-r border-b border-slate-300"} border-b`}>
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-[11px] mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
      active ? "border-[#1D2951] text-[#1D2951]" : "border-transparent text-slate-500 hover:text-slate-800"
    }`}
  >
    {children}
  </button>
);

const SectionTitle = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#1D2951] border-b border-slate-200 pb-1 pt-2">{children}</div>
);
